import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Select } from './ui/select';
import { Label } from './ui/label';
import { AlertCircle, CheckCircle, Save, RotateCcw, Palette, Globe, Clock, Sun, Moon, Monitor } from 'lucide-react';
import { updateUserProfile } from '../services/api';
import { useUser } from '../hooks/useUser';

export const UserPreferencesTab: React.FC = () => {
  const { user: userData, updateUser } = useUser();
  const [formData, setFormData] = useState({
    theme: 'system' as 'light' | 'dark' | 'system',
    language: 'pt' as 'pt' | 'en',
    timezone: 'America/Sao_Paulo'
  });
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Timezone options
  const timezoneOptions = [
    { value: 'America/Sao_Paulo', label: 'São Paulo (GMT-3)' },
    { value: 'America/New_York', label: 'New York (GMT-5)' },
    { value: 'Europe/London', label: 'London (GMT+0)' },
    { value: 'Europe/Paris', label: 'Paris (GMT+1)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (GMT+9)' },
    { value: 'UTC', label: 'UTC (GMT+0)' }
  ];

  useEffect(() => {
    if (userData) {
      setFormData({
        theme: userData.theme || 'system',
        language: userData.language || 'pt',
        timezone: userData.timezone || 'America/Sao_Paulo'
      });
    }
  }, [userData]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear success message when user starts editing
    if (saveSuccess) {
      setSaveSuccess(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const response = await updateUserProfile(formData);
      
      // Update user data in context
      updateUser(response.data.data.user);
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao salvar preferências');
      console.error('Error saving preferences:', err);
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = () => {
    if (!userData) return false;
    
    return (
      formData.theme !== (userData.theme || 'system') ||
      formData.language !== (userData.language || 'pt') ||
      formData.timezone !== (userData.timezone || 'America/Sao_Paulo')
    );
  };

  const getThemeIcon = (theme: string) => {
    switch (theme) {
      case 'light': return <Sun className="w-4 h-4" />;
      case 'dark': return <Moon className="w-4 h-4" />;
      default: return <Monitor className="w-4 h-4" />;
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Appearance */}
        <Card variant="glass">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="w-4 h-4" />
              Aparência
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="theme">Tema do Sistema</Label>
              <Select 
                value={formData.theme} 
                onChange={(value) => handleInputChange('theme', value as 'light' | 'dark' | 'system')}
                options={[
                  { value: 'system', label: 'Sistema' },
                  { value: 'light', label: 'Claro' },
                  { value: 'dark', label: 'Escuro' }
                ]}
                placeholder="Selecione o tema"
                className="mt-1"
              />
              <p className="text-xs text-foreground-tertiary mt-1">
                O tema será aplicado em toda a aplicação
              </p>
            </div>

            <div className="p-4 bg-white/5 rounded-lg border border-white/10">
              <div className="flex items-center gap-2 mb-2">
                {getThemeIcon(formData.theme)}
                <span className="text-sm font-medium text-foreground-primary">
                  Tema Atual: {formData.theme === 'system' ? 'Sistema' : formData.theme === 'light' ? 'Claro' : 'Escuro'}
                </span>
              </div>
              <p className="text-xs text-foreground-tertiary">
                {formData.theme === 'system' 
                  ? 'Segue as configurações do sistema operacional'
                  : formData.theme === 'light'
                  ? 'Interface clara com fundo branco'
                  : 'Interface escura com fundo preto (atual)'
                }
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Language & Region */}
        <Card variant="glass">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="w-4 h-4" />
              Idioma e Região
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="language">Idioma da Interface</Label>
              <Select 
                value={formData.language} 
                onChange={(value) => handleInputChange('language', value as 'pt' | 'en')}
                options={[
                  { value: 'pt', label: 'Português (Brasil)' },
                  { value: 'en', label: 'English (United States)' }
                ]}
                placeholder="Selecione o idioma"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="timezone">Fuso Horário</Label>
              <Select 
                value={formData.timezone} 
                onChange={(value) => handleInputChange('timezone', value)}
                options={timezoneOptions}
                placeholder="Selecione o fuso horário"
                className="mt-1"
              />
              <p className="text-xs text-foreground-tertiary mt-1">
                Usado para exibir datas e horários corretos
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Information */}
      <Card variant="glass">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Informações do Sistema
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-foreground-secondary">Horário atual:</span>
              <span className="text-foreground-primary">
                {new Date().toLocaleString('pt-BR', { 
                  timeZone: formData.timezone,
                  dateStyle: 'short',
                  timeStyle: 'medium'
                })}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-foreground-secondary">Fuso horário:</span>
              <span className="text-foreground-primary">
                {timezoneOptions.find(tz => tz.value === formData.timezone)?.label || formData.timezone}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-foreground-secondary">Idioma:</span>
              <span className="text-foreground-primary">
                {formData.language === 'pt' ? 'Português (Brasil)' : 'English (United States)'}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex items-center gap-3 pt-4">
        <Button
          onClick={handleSave}
          disabled={saving || !hasChanges()}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {saving ? (
            <RotateCcw className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Salvar Preferências
        </Button>

        {saveSuccess && (
          <div className="flex items-center text-green-400 text-sm">
            <CheckCircle className="w-4 h-4 mr-1" />
            Preferências salvas com sucesso!
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-400">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm font-medium">{error}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserPreferencesTab;